import { useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import "./styles.scss";
import classNames from "classnames";
import { WidgetContainerProps } from "../../../utils/asset-dashboard/types";
import {
  ActionTypes,
  useWidgetContextDispatch,
} from "../../../hooks/use-widget-context";
import { Box, Paper } from "@mui/material";

export const WidgetContainer = ({
  id,
  position,
  config,
  children,
  draggable = true,
  animatable = true,
}: WidgetContainerProps) => {
  const widgetDispatch = useWidgetContextDispatch();

  const [animate, setAnimate] = useState(false);

  const [{ opacity }, dragRef] = useDrag(
    () => ({
      type: "widget",
      item: { id, position, config },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
      }),
    }),
    []
  );

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "widget",
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    drop: (item: any) => {
      widgetDispatch({
        type: ActionTypes.SWAP_WIDGETS,
        payload: {
          source: item.id,
          destination: id,
        },
      });
    },
  }));

  useEffect(() => {
    animatable && setAnimate(true);
  }, [position.index, animatable]);

  return (
    <Box
      px={"4px"}
      sx={{
        mt: 1,
        // Grid items default to min-width: auto (content-based), so a wide
        // chart/table in one row can force this column track wider for
        // every row sharing it. minWidth: 0 lets the track follow gridTemplateColumns instead.
        minWidth: 0,
        // Native CSS Grid column span — clamped to however many columns are
        // actually available at each breakpoint, replacing the old
        // JS-measured (getBoundingClientRect + resize listener) pixel width.
        // xs always spans the full 2-column row (1 widget per row on mobile)
        // regardless of position.width, so narrow chart widgets no longer
        // squeeze two-per-row and shrink their own content unreadably.
        gridColumn: {
          xs: "span 2",
          sm: `span ${Math.min(3, position.width)}`,
          md: `span ${Math.min(4, position.width)}`,
        },
      }}
    >
      <Paper
        elevation={3}
        onAnimationEnd={() => animate && setAnimate(false)}
        style={{
          background: isOver ? "lightblue" : "",
          height: "100%",
        }}
        className={classNames(animate && animatable && "animate")}
        ref={draggable ? (drop as any) : null}
      >
        <div
          className="draggable-content"
          ref={draggable ? (dragRef as any) : null}
          style={{ opacity }}
        >
          {children}
        </div>
      </Paper>
    </Box>
  );
};

export default WidgetContainer;
